import { useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  FeatureToggleKeys,
  FeatureToggleDefaults,
  type FeatureToggleKeyType,
} from '@/services/feature-toggle-keys';
import {
  fetchRemoteConfig,
  getRemoteConfigBoolean,
  BooleanKeys,
} from '@/services/firebase.config';

// Re-export keys and types for convenience
export { FeatureToggleKeys } from '@/services/feature-toggle-keys';
export type { FeatureToggleKeyType } from '@/services/feature-toggle-keys';

// Legacy export for backward compatibility
export const FEATURE_FLAGS = FeatureToggleKeys;

type FeatureFlagKey = FeatureToggleKeyType;

interface FeatureFlags {
  [key: string]: boolean;
}

// Query key for feature toggles
const FEATURE_TOGGLE_QUERY_KEY = ['featureToggles'];

// Hook to fetch and cache feature toggles from Firebase Remote Config
export const useFeatureToggleQuery = () => {
  return useQuery({
    queryKey: FEATURE_TOGGLE_QUERY_KEY,
    queryFn: async (): Promise<FeatureFlags> => {
      try {
        // Fetch and activate remote config
        await fetchRemoteConfig();

        // Get all feature toggle values
        const flags: FeatureFlags = {};
        for (const key of Object.values(FeatureToggleKeys)) {
          try {
            // Feature toggles map directly to remote config boolean keys
            flags[key] = getRemoteConfigBoolean(key as keyof typeof BooleanKeys);
          } catch {
            // Use default value if remote config fails for this key
            flags[key] = FeatureToggleDefaults[key as FeatureToggleKeyType] ?? false;
          }
        }
        return flags;
      } catch (error) {
        console.error('Error fetching feature toggles:', error);
        // Return defaults if Firebase fails
        return { ...FeatureToggleDefaults };
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
  });
};

// Main hook for checking feature toggles
export const useFeatureToggle = () => {
  const { data: remoteFlags, isLoading } = useFeatureToggleQuery();

  // Merge remote flags with defaults
  const flags = useMemo(() => {
    return { ...FeatureToggleDefaults, ...remoteFlags };
  }, [remoteFlags]);

  // Check if a single feature is enabled
  const isEnabled = useCallback(
    (key: FeatureFlagKey): boolean => {
      return flags[key] ?? FeatureToggleDefaults[key] ?? false;
    },
    [flags]
  );

  // Check if any of the provided features are enabled
  const isAnyEnabled = useCallback(
    (keys: FeatureFlagKey[]): boolean => {
      return keys.some((key) => isEnabled(key));
    },
    [isEnabled]
  );

  // Check if all of the provided features are enabled
  const areAllEnabled = useCallback(
    (keys: FeatureFlagKey[]): boolean => {
      return keys.every((key) => isEnabled(key));
    },
    [isEnabled]
  );

  // Check if none of the provided features are enabled
  const areNoneEnabled = useCallback(
    (keys: FeatureFlagKey[]): boolean => {
      return keys.every((key) => !isEnabled(key));
    },
    [isEnabled]
  );

  return {
    flags,
    isEnabled,
    isAnyEnabled,
    areAllEnabled,
    areNoneEnabled,
    isLoading,
  };
};

// Standalone function to check feature (for use outside React components)
export const checkFeatureEnabled = (key: FeatureToggleKeyType): boolean => {
  try {
    return getRemoteConfigBoolean(key as keyof typeof BooleanKeys);
  } catch {
    return FeatureToggleDefaults[key] ?? false;
  }
};

export default useFeatureToggle;
